import React, { useEffect, useState } from 'react';
import { auth, apiBaseUrl } from '../firebase';
import { generateConversionPixel } from '../utils/conversions';
import { getConversionPixelConfig, listConversions, listMyAds } from '../utils/secureFunctions';

interface Conversion {
  id: string;
  adId: string;
  conversionType: string;
  conversionValue: number;
  metadata?: any;
  createdAt: any;
}

interface AdConversionData {
  adId: string;
  adTitle: string;
  conversions: number;
  totalValue: number;
  conversionRate: number; // clicks to conversions
}

const ConversionsDashboard: React.FC = () => {
  const [conversions, setConversions] = useState<Conversion[]>([]);
  const [adStats, setAdStats] = useState<AdConversionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPixelCode, setShowPixelCode] = useState(false);
  const [pixelEnabled, setPixelEnabled] = useState<boolean | null>(null);
  const pixelApiBase = apiBaseUrl || 'https://api.belivah.com';

  useEffect(() => {
    if (!auth.currentUser) return;
    loadConversions();
    getConversionPixelConfig().then((r) => setPixelEnabled(!!r.enabled)).catch(() => setPixelEnabled(false));
  }, []);

  const loadConversions = async () => {
    try {
      if (!auth.currentUser) return;

      const [conversionsRes, adsRes] = await Promise.all([
        listConversions(),
        listMyAds(),
      ]);

      const convs = (conversionsRes.conversions || []) as Conversion[];
      setConversions(convs);

      const ads = adsRes.ads || [];
      const adsMap = new Map<string, any>(ads.map((ad: any) => [ad.id, ad]));

      const statsMap = new Map<string, any>();
      for (const conv of convs) {
        const ad = adsMap.get(conv.adId);
        if (!statsMap.has(conv.adId)) {
          statsMap.set(conv.adId, {
            adId: conv.adId,
            adTitle: ad?.title || 'Unknown Ad',
            conversions: 0,
            totalValue: 0,
            clicks: ad?.clicks || 0,
          });
        }

        const stat = statsMap.get(conv.adId);
        stat.conversions += 1;
        stat.totalValue += conv.conversionValue || 0;
      }

      const stats = Array.from(statsMap.values()).map((s: any) => ({
        ...s,
        conversionRate: s.clicks > 0 ? (s.conversions / s.clicks * 100).toFixed(2) : 0,
      }));

      setAdStats(stats);
    } catch (error) {
      console.error('Failed to load conversions:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyPixelCode = () => {
    const code = generateConversionPixel(auth.currentUser?.uid || '', pixelApiBase);
    navigator.clipboard.writeText(code);
    alert('Conversion pixel code copied to clipboard!');
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading conversion data...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Conversion Tracking</h1>
        <button style={styles.pixelBtn} onClick={() => setShowPixelCode(!showPixelCode)}>
          {showPixelCode ? 'Hide' : 'Show'} Pixel Code
        </button>
      </div>

      {showPixelCode && (
        <div style={styles.pixelBox}>
          <div style={styles.pixelHeader}>
            <h3 style={{ margin: 0 }}>Conversion Tracking Pixel</h3>
            <button style={styles.copyBtn} onClick={copyPixelCode}>
              Copy Code
            </button>
          </div>
          <p style={styles.pixelInstruction}>
            {pixelEnabled === false
              ? 'Conversion tracking is temporarily unavailable. Please try again later.'
              : 'Paste this code on every page of your website. When someone arrives from your Belivah ad, the pixel remembers the click for 30 days. Call BelivahConversion.track() when they complete an action (purchase, signup, lead) and it will appear here.'}
          </p>
          <pre style={styles.codeBlock}>
            {generateConversionPixel(auth.currentUser?.uid || '', pixelApiBase)}
          </pre>
          <p style={styles.pixelUsage}>
            <strong>Usage Example:</strong><br />
            <code style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}>{'<script>BelivahConversion.track("purchase", 49.99, {product_id: "abc123"});</script>'}</code>
          </p>
        </div>
      )}

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Conversion Summary by Ad</h2>
        {adStats.length === 0 ? (
          <div style={styles.empty}>No conversions tracked yet</div>
        ) : (
          <div style={styles.statsGrid}>
            {adStats.map(stat => (
              <div key={stat.adId} style={styles.statCard}>
                <div style={styles.statTitle}>{stat.adTitle}</div>
                <div style={styles.statRow}>
                  <div>
                    <div style={styles.statLabel}>Conversions</div>
                    <div style={styles.statValue}>{stat.conversions}</div>
                  </div>
                  <div>
                    <div style={styles.statLabel}>Total Value</div>
                    <div style={styles.statValue}>${stat.totalValue.toFixed(2)}</div>
                  </div>
                  <div>
                    <div style={styles.statLabel}>Conversion Rate</div>
                    <div style={styles.statValue}>{stat.conversionRate}%</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Recent Conversions</h2>
        {conversions.length === 0 ? (
          <div style={styles.empty}>No conversions yet</div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.headerRow}>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Value</th>
                  <th style={styles.th}>Ad ID</th>
                </tr>
              </thead>
              <tbody>
                {conversions.slice(0, 50).map(conv => (
                  <tr key={conv.id} style={styles.row}>
                    <td style={styles.td}>
                      {conv.createdAt ? new Date(conv.createdAt as any).toLocaleString() : 'N/A'}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.badge}>{conv.conversionType}</span>
                    </td>
                    <td style={styles.td}>${conv.conversionValue?.toFixed(2) || '0.00'}</td>
                    <td style={styles.td}>
                      <code style={styles.code}>{conv.adId.substring(0, 8)}...</code>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: 'clamp(12px, 4vw, 30px)',
    maxWidth: '1200px',
    width: '100%',
    minWidth: 0,
    margin: '0 auto',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: '30px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
  },
  pixelBtn: {
    padding: '10px 20px',
    backgroundColor: '#4caf50',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px',
  },
  pixelBox: {
    backgroundColor: '#f9f9f9',
    padding: 'clamp(12px, 4vw, 20px)',
    borderRadius: '8px',
    marginBottom: '30px',
    border: '1px solid #ddd',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  pixelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: '10px',
  },
  copyBtn: {
    padding: '6px 12px',
    backgroundColor: '#2196f3',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  pixelInstruction: {
    color: '#666',
    fontSize: '14px',
    marginBottom: '10px',
  },
  codeBlock: {
    backgroundColor: '#2d2d2d',
    color: '#f8f8f2',
    padding: '15px',
    borderRadius: '5px',
    overflow: 'auto',
    fontSize: '12px',
    maxHeight: '300px',
    maxWidth: '100%',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    boxSizing: 'border-box',
  },
  pixelUsage: {
    marginTop: '10px',
    fontSize: '13px',
    color: '#555',
  },
  section: {
    marginBottom: '40px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#444',
    marginBottom: '20px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
    gap: '20px',
  },
  statCard: {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  statTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '15px',
    color: '#333',
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '15px',
  },
  statLabel: {
    fontSize: '12px',
    color: '#666',
    marginBottom: '5px',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#2196f3',
  },
  loading: {
    textAlign: 'center',
    padding: '50px',
    color: '#666',
  },
  empty: {
    textAlign: 'center',
    padding: '50px',
    color: '#999',
    fontSize: '16px',
  },
  tableContainer: {
    overflowX: 'auto',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  headerRow: {
    backgroundColor: '#f5f5f5',
  },
  th: {
    padding: '15px',
    textAlign: 'left',
    fontWeight: 'bold',
    color: '#555',
    borderBottom: '2px solid #ddd',
  },
  row: {
    borderBottom: '1px solid #eee',
  },
  td: {
    padding: '15px',
    color: '#333',
  },
  badge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold',
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
  },
  code: {
    padding: '2px 6px',
    backgroundColor: '#f5f5f5',
    borderRadius: '3px',
    fontFamily: 'monospace',
    fontSize: '12px',
  },
};

export default ConversionsDashboard;
